import { useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { errorMessage } from '@/shared/api/httpClient'
import { roomsApi, type Room } from '@/shared/api/roomsApi'
import { useAsync } from '@/shared/api/useAsync'
import { displayName, useAuth } from '@/shared/auth/useAuth'
import { Blueprint, Corners } from '@/shared/ui/Blueprint'
import { Dialog } from '@/shared/ui/Dialog'
import { devicesApi } from '@/features/devices/devicesApi'
import type { Device, Kind } from '@/features/devices/types'

const svg = { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' } as const
const ICONS: Record<Kind, ReactNode> = {
  luz: <svg {...svg}><path d="M9 18h6" /><path d="M10 21h4" /><path d="M12 3a6 6 0 0 0-3.5 10.9V18h7v-4.1A6 6 0 0 0 12 3Z" /></svg>,
  tomada: <svg {...svg}><rect x="5" y="8" width="14" height="13" rx="1" /><path d="M9 8V3" /><path d="M15 8V3" /><path d="M12 13v3" /></svg>,
  termostato: <svg {...svg}><path d="M14 14.8V4a2 2 0 1 0-4 0v10.8a4 4 0 1 0 4 0Z" /></svg>,
  interruptor: <svg {...svg}><rect x="4" y="6" width="16" height="12" rx="1" /><path d="M12 6v12" /></svg>,
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

export function DevicesPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const notice = (useLocation().state as { notice?: string } | null)?.notice

  const { data, setData, error, loading } = useAsync(async () => {
    const [rooms, devices, agents] = await Promise.all([roomsApi.list(), devicesApi.list(), devicesApi.agentCount()])
    return { rooms, devices, agents }
  })

  const [filter, setFilter] = useState('todos') // 'todos' | id do cômodo
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null)
  const [menuId, setMenuId] = useState<number | null>(null)
  const [newRoom, setNewRoom] = useState<string | null>(null) // null = diálogo fechado
  const [moving, setMoving] = useState<{ device: Device; room: string } | null>(null)
  const [removing, setRemoving] = useState<Device | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  if (loading) return <p className="page-state">Carregando…</p>
  if (!data) return <p className="page-state">{error}</p>

  const { rooms, devices, agents } = data
  const patchRooms = (fn: (rooms: Room[]) => Room[]) => setData((d) => d && { ...d, rooms: fn(d.rooms) })
  const patchDevice = (device: Device) =>
    setData((d) => d && { ...d, devices: d.devices.map((x) => (x.id === device.id ? device : x)) })

  const sections: { room: Room | null; devices: Device[] }[] = rooms
    .filter((r) => filter === 'todos' || String(r.id) === filter)
    .map((room) => ({ room, devices: devices.filter((d) => d.room?.id === room.id) }))
  const orphans = devices.filter((d) => !d.room)
  if (filter === 'todos' && orphans.length) sections.push({ room: null, devices: orphans })

  async function attempt(fn: () => Promise<void>) {
    setActionError(null)
    try {
      await fn()
    } catch (err) {
      setActionError(errorMessage(err))
    }
  }

  const createRoom = () =>
    attempt(async () => {
      const name = (newRoom ?? '').trim()
      if (!name) return
      const room = await roomsApi.create(name)
      patchRooms((rs) => [...rs, room])
      setFilter(String(room.id))
      setNewRoom(null)
    })

  const saveRename = () =>
    attempt(async () => {
      if (!renaming) return
      const name = renaming.name.trim()
      if (name) {
        const room = await roomsApi.rename(renaming.id, name)
        patchRooms((rs) => rs.map((r) => (r.id === room.id ? room : r)))
      }
      setRenaming(null)
    })

  const removeRoom = (room: Room) =>
    attempt(async () => {
      await roomsApi.remove(room.id)
      patchRooms((rs) => rs.filter((r) => r.id !== room.id))
      if (filter === String(room.id)) setFilter('todos')
    })

  const confirmMove = () =>
    attempt(async () => {
      if (!moving) return
      patchDevice(await devicesApi.move(moving.device.id, moving.room ? Number(moving.room) : null))
      setMoving(null)
    })

  const confirmRemove = () =>
    attempt(async () => {
      if (!removing) return
      await devicesApi.remove(removing.id)
      setData((d) => d && { ...d, devices: d.devices.filter((x) => x.id !== removing.id) })
      setRemoving(null)
    })

  const open = (d: Device) => navigate(`/dispositivos/${d.id}`)

  return (
    <div>
      {notice && (
        <Blueprint
          style={{ padding: '12px 14px', marginBottom: 20, borderColor: 'var(--color-accent)', background: 'var(--color-accent-100)', display: 'flex', alignItems: 'center', gap: 12 }}
        >
          <span className="heading" style={{ fontSize: 15, color: 'var(--color-accent-800)' }}>{notice}</span>
        </Blueprint>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: 6 }}>
        <div>
          <h2 style={{ margin: 0 }}>O que vamos ligar hoje, {displayName(user)}?</h2>
          <div className="text-muted" style={{ fontSize: 13 }}>
            {plural(devices.length, 'dispositivo', 'dispositivos')} · {plural(rooms.length, 'cômodo', 'cômodos')} ·{' '}
            {plural(agents, 'agente conectado', 'agentes conectados')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', flex: '1 1 300px' }}>
          <div className="field" style={{ flex: '1 1 170px', minWidth: 150 }}>
            <label htmlFor="room-filter">Cômodo</label>
            <select id="room-filter" className="input" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="todos">Todos</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => setNewRoom('')} style={{ minHeight: 42 }}>
            + Cômodo
          </button>
        </div>
      </div>

      {actionError && <p className="form-error" style={{ marginTop: 10 }}>{actionError}</p>}

      {sections.map(({ room, devices: list }) => {
        const empty = list.length === 0
        const editing = room && renaming?.id === room.id
        return (
          <section key={room?.id ?? 'sem-comodo'} style={{ marginTop: 34 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 8, borderBottom: '1px solid var(--color-divider)', marginBottom: 18 }}>
              {editing ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    className="input"
                    type="text"
                    aria-label="Nome do cômodo"
                    autoFocus
                    value={renaming.name}
                    onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename()
                      if (e.key === 'Escape') setRenaming(null)
                    }}
                    style={{ width: 200, fontFamily: 'var(--font-heading)', fontSize: 18 }}
                  />
                  <button type="button" className="btn btn-primary" onClick={saveRename}>Salvar</button>
                </div>
              ) : (
                <h4 className={empty ? 'text-muted' : undefined} style={{ margin: 0 }}>{room?.name ?? 'Sem cômodo'}</h4>
              )}
              <span className="kicker" style={{ fontSize: 12 }}>{plural(list.length, 'dispositivo', 'dispositivos')}</span>
              <div style={{ flex: 1 }} />
              {room && !editing && (
                <button type="button" className="btn btn-ghost" onClick={() => setRenaming({ id: room.id, name: room.name })}>Renomear</button>
              )}
              {room && empty && (
                <button type="button" className="btn btn-ghost" onClick={() => removeRoom(room)}>Excluir</button>
              )}
            </div>

            {empty && (
              <Blueprint style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', borderStyle: 'dashed' }}>
                <span className="text-muted" style={{ fontSize: 14 }}>Cômodo criado, ainda sem dispositivos.</span>
                <div style={{ flex: 1 }} />
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/dispositivos/adicionar')}>Buscar dispositivos</button>
              </Blueprint>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,196px),1fr))', gap: 'clamp(12px,2vw,18px)' }}>
              {list.map((d) => (
                <div
                  key={d.id}
                  className="card blueprint"
                  role="link"
                  tabIndex={0}
                  onClick={() => open(d)}
                  onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && open(d)}
                  style={{ cursor: 'pointer', padding: 14, gap: 10, flexDirection: 'row', alignItems: 'center', position: 'relative' }}
                >
                  <Corners />
                  <div style={{ color: 'var(--color-accent-700)', flex: 'none' }}>{ICONS[d.kind]}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="card-title" style={{ fontSize: 16 }}>{d.name}</div>
                    <div className="card-kicker">{d.kind_label}</div>
                  </div>
                  {d.fault ? (
                    <span className="text-muted" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {d.fault === 'auth' ? 'reautenticar' : 'offline'}
                      <span style={{ width: 9, height: 9, flex: 'none', border: '1.5px solid var(--color-neutral-600)', borderRadius: '50%' }} />
                    </span>
                  ) : (
                    <span style={{ width: 9, height: 9, flex: 'none', background: 'var(--color-accent)', borderRadius: '50%' }} />
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    aria-label={`Opções de ${d.name}`}
                    aria-expanded={menuId === d.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      setMenuId((m) => (m === d.id ? null : d.id))
                    }}
                    style={{ width: 44, height: 44, padding: 0, flex: 'none', fontSize: 16, lineHeight: 1 }}
                  >
                    ⋮
                  </button>
                  {menuId === d.id && (
                    <Blueprint className="elev-md" style={{ position: 'absolute', right: 10, top: 44, zIndex: 4, background: 'var(--color-bg)', padding: 6, minWidth: 170 }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-block"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuId(null)
                          setMoving({ device: d, room: d.room ? String(d.room.id) : '' })
                        }}
                        style={{ margin: 0, justifyContent: 'flex-start', border: 0 }}
                      >
                        Mover de cômodo
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-block"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuId(null)
                          setRemoving(d)
                        }}
                        style={{ margin: 0, justifyContent: 'flex-start', border: 0 }}
                      >
                        Remover
                      </button>
                    </Blueprint>
                  )}
                </div>
              ))}
            </div>
          </section>
        )
      })}

      {newRoom !== null && (
        <Dialog
          title="Novo cômodo"
          onClose={() => setNewRoom(null)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setNewRoom(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={createRoom} disabled={!newRoom.trim()}>Criar cômodo</button>
            </>
          }
        >
          <div className="field">
            <label htmlFor="new-room-name">Nome</label>
            <input
              id="new-room-name"
              className="input"
              type="text"
              autoFocus
              placeholder="Ex.: Lavanderia"
              value={newRoom}
              onChange={(e) => setNewRoom(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createRoom()}
            />
          </div>
          <p className="text-muted" style={{ fontSize: 12, margin: '10px 0 0' }}>
            O cômodo entra na hora como filtro de <span className="mono">listar_dispositivos</span>.
          </p>
          {actionError && <p className="form-error" style={{ marginTop: 10 }}>{actionError}</p>}
        </Dialog>
      )}

      {removing && (
        <Dialog
          title={`Remover ${removing.name}?`}
          onClose={() => setRemoving(null)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setRemoving(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={confirmRemove}>Remover</button>
            </>
          }
        >
          <p style={{ fontSize: 14, margin: 0 }}>
            Sai do console e de <span className="mono">listar_dispositivos</span>. O aparelho continua na sua rede e pode ser adicionado de novo pela busca.
          </p>
          {actionError && <p className="form-error" style={{ marginTop: 10 }}>{actionError}</p>}
        </Dialog>
      )}

      {moving && (
        <Dialog
          title={`Mover ${moving.device.name}`}
          onClose={() => setMoving(null)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setMoving(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={confirmMove}>Mover</button>
            </>
          }
        >
          <div className="field">
            <label htmlFor="move-room">Cômodo</label>
            <select id="move-room" className="input" value={moving.room} onChange={(e) => setMoving({ ...moving, room: e.target.value })}>
              <option value="">Sem cômodo</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          {actionError && <p className="form-error" style={{ marginTop: 10 }}>{actionError}</p>}
        </Dialog>
      )}
    </div>
  )
}
